import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
    Box,
    Button,
    SvgIcon,
    CircularProgress,
    Menu,
    MenuItem,
    inputBaseClasses,
    inputLabelClasses,
    ClickAwayListener,
    Tooltip,
    Typography,
    useTheme,
    useMediaQuery,
  } from '@mui/material';
import { CSVLink } from 'react-csv'
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { getLocalTimeZoneAndOffset } from 'src/api/DateFormatter';
import {saveAs} from 'file-saver';
import useAuth from 'src/hooks/useAuth';
import { btcInfoWebsite } from 'src/config';
import "@ux-engineering/metal/css/metal.css";
import useSelectedGroup from 'src/hooks/useSelectedGroup';
import { utils, writeFile } from 'xlsx';
import { enqueueSnackbar } from 'notistack';
import { InfoIcon } from 'lucide-react';
import JSZip from 'jszip';

const useStyles = makeStyles()((theme) => ({
  actionIcon: {
    marginRight: theme.spacing(0.5)
  },
  exportSelect: {
    transform: 'translate(14px, 9px) scale(1) !important',
    pointerEvents: 'none !important',
    maxWidth: 'calc(100% - 24px) !important'
  },
  redesignBtn: {
    borderRadius: 'var(--radius-xs, 4px) !important',
    background: 'var(--Component-colors-Components-Buttons-Primary-button-primary-bg, #00B161) !important',
    padding: '10px 14px !important',
    color: '#FFFFFF !important',
    textTransform: 'capitalize',
    fontWeight: 600,
    height: '40px !important',
    boxShadow: 'none',
    "&.Mui-disabled":{
      color: "rgba(255, 255, 255, 0.26) !important",
      boxShadow: "none",
      backgroundColor: "rgba(255, 255, 255, 0.12) !important"
    },
    "& svg":{
      position: "relative",
      top: "2px",
      marginRight: "4px"
    },
    "& .arrowLeft, & .arrowRight": {
      transform: "translate(0px, 0px)",
      transition: "all 0.5s ease",
    },
    '&:hover, &.Mui-focusVisible': {
      cursor: "pointer",
      backgroundColor: 'var(--accent-5)',
      "& .arrowLeft, & .arrowRight": {
        transform: "translate(0px, 3px)",
        transition: "all 0.5s ease",
      }
    },
  },
  redesignSecondaryBtn: {
    height: '40px !important',
    backgroundColor: 'rgba(0,0,0,0)',
    color: 'var(--accent-11)',
    fontSize: "14px",
    fontWeight: "600",
    textTransform: "capitalize",
    boxShadow: 'none',
    "& svg":{
      position: "relative",
      top: "2px",
      marginRight: "4px"
    },
    "& .arrowLeft, & .arrowRight": {
      transform: "translate(0px, 0px)",
      transition: "all 0.5s ease",
    },
    '&:hover, &.Mui-focusVisible': {
      cursor: "pointer",
      backgroundColor: 'var(--accent-5)',
      "& .arrowLeft, & .arrowRight": {
        transform: "translate(0px, 3px)",
        transition: "all 0.5s ease",
      }
    }
  },
  inputLabel: {
    display: "flex",
    width: "100%",
    justifyContent: "center",
    '&.MuiInputLabel-root.Mui-focused': {
      color: 'var(--accent-11)'
    }
  }
}));

const ExportCSVComponent = (props) => {
    const { t } = useTranslation();
    const { classes, cx } = useStyles();
    const [csvData, setCsvData] = useState([])
    const csvLink = useRef();
    const [selectedExportOption, setSelectedExportOption] = useState('');
    const [open, setOpen] = useState(false);
    const [buildingPdf, setBuildingPdf] = useState(false)
    const { loggedInUser } = useAuth();
    const [exportFocused, setExportFocused] = useState(false);
    const { selectedGroup } = useSelectedGroup();
    const workerRef = useRef();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const menuOpen = Boolean(anchorEl);
    const dropdownRef = useRef();
    const labelRef = useRef();
    const {
      exportInProcess,
      fileName,
      singleDate,
      reportDate,
      startDate,
      endDate,
      subAccount,
      reportName,
      groupReport,
      pdfOption,
      pdfDisabledReason,
      xlsxOption,
      getCSVData,
      disableExport,
      multiFileExport = false
    } = props;
    const [showExportInfo, setShowExportInfo] = useState(false);
    const theme = useTheme();
    const mobileDevice = useMediaQuery(theme.breakpoints.down('md'));
    const [multiCsvData, setMultiCsvData] = useState([]);

    const handleMenuClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };

    const handleExportButtonClick = () => {
      setSelectedExportOption('csv')
      setCsvData([])
      getExportData('csv')
    }
    const handleExportMenuUpdate = (option) => {
        handleMenuClose(false)
        switch (option) {
          case "pdf":
            setBuildingPdf(true)
        
          default:
            getExportData(option)
            setSelectedExportOption(option)   
            break;
        }
    }
    const getExportData = async (exportType) => {
        const data = (reportName === 'Financial Overview Report' || 'Sub-Account Financials Report') && exportType === 'pdf' ? await getCSVData('pdf') : await getCSVData();
        if(data && data.length !== 0 && (exportType === 'csv' || exportType === 'xlsx') && multiFileExport){
           setMultiCsvData(data);
       } else if(data && data.length !== 0 && (exportType === 'csv' || exportType === 'xlsx' || exportType === 'tsv') && !multiFileExport){
          setCsvData(data);
        } else if (data && data.length !== 0 && exportType === 'pdf' && (reportName === 'Transactions Report')) {
          const pdfData = data.map(d => {
            return {
              ...d,
              'Transaction id':  d['Transaction id'] ? {
                display: `${d['Transaction id'].slice(0,4)}...${d['Transaction id'].slice(-4)}`,
                url: `${btcInfoWebsite}/tx/${d['Transaction id']}`
              } : { display: '', url: ''},
              Address: d.Address ? { 
                display: `${d.Address.slice(0,4)}...${d.Address.slice(-4)}`, 
                url: `${btcInfoWebsite}/address/${d.Address}`
              } : { display: '', url: ''}
            }
          })
          setCsvData(pdfData)
        } else if (data && data.length !== 0 && exportType === 'pdf' && (reportName === 'Sub-Accounts Report')) {
          const pdfData = data.map(d => {
            return {
              'Name': d['Name'],
              'Hashrate1hrAvg': d['Hashrate1hrAvg'],
              'Active Workers': d['Active Workers'],
              'Offline (>15 Min)': d['Offline (>15 Min)'],
              'Offline (>24 Hr)': d['Offline (>24 Hr)']
            }
          })
          setCsvData(pdfData)
        } else if (data && data.length !== 0 && exportType === 'pdf' &&
          (reportName !== 'Transactions Report' || reportName === 'Sub-Accounts Report' || reportName === 'Sub-Account Financials Report')) {
          setCsvData(data)
        } else {
          setCsvData([])
          setSelectedExportOption('')
          setBuildingPdf(false)
          resetExportDropdown();
        }
    }

    const getFileName = () => {
      if (singleDate) {
        return `${fileName} ${dayjs().format('YYYY-MM-DDTHH_mm_ss')}${getLocalTimeZoneAndOffset().split(' ')[0].replace(':','_')}`;
      } else if (!singleDate && (startDate && endDate) && (startDate === endDate)) {
        return `${fileName} ${startDate}`;
      } else if (startDate && endDate) {
        return `${fileName} ${dayjs(startDate).format('YYYY-MM-DDTHH_mm')} ${getLocalTimeZoneAndOffset()} – ${dayjs(endDate).format('YYYY-MM-DDTHH_mm')} ${getLocalTimeZoneAndOffset()}`;
      } else {
        return `${fileName} ${dayjs().format('YYYY-MM-DDTHH_mm_ss')}${getLocalTimeZoneAndOffset().split(' ')[0].replace(':','_')}`;
      }
    }

    const getXLSXFileName = () => {
      if (singleDate) {
        return `${fileName} ${dayjs().format('YYYY-MM-DDTHH_mm_ss')}`;
      } else {
        return `${fileName} ${dayjs(startDate).format('YYYY-MM-DD')}–${dayjs(endDate).format('YYYY-MM-DD')}`;
      }
    }

    const getMultipleDatesStr = () => {
      const start = dayjs(startDate);
      const end = dayjs(endDate);
      return `${start.format('MMMM DD, YYYY')} - ${end.format('MMMM DD, YYYY')}`
    }

    const getDateRangeWithUTCOffset = () => {
      return `${dayjs(startDate).format('YYYY-MM-DDTHH_mm')} ${getLocalTimeZoneAndOffset()} – ${dayjs(endDate).format('YYYY-MM-DDTHH_mm')} ${getLocalTimeZoneAndOffset()}`;
    }

    useEffect(() => {
      // This logic prevents multiple web workers created for generating pdfs
      if (!workerRef.current) {
       workerRef.current = new Worker(new URL('src/webWorker.js', import.meta.url), { type: 'module'});

       workerRef.current.onmessage = (event) => {
          const { type, payload, pdfName } = event.data;
          if (type === 'pdfGenerated') {
            saveAs(payload, `${pdfName}.pdf`);
          } else if (type === 'pdfError') {
            enqueueSnackbar(payload, {
              variant: 'error'
            })
          }
            setBuildingPdf(false);
            setSelectedExportOption('');
            resetExportDropdown();
         }
      }

    }, [])

    const createPdf = async () => {
     if (csvData && csvData.length && !open) {

       const groupNames = groupReport ? `${selectedGroup?.length > 1 ? selectedGroup?.map(g => g.group.name).join(', ') 
        : selectedGroup?.map(g => g.group.name)[0]}` : `${loggedInUser?.groupName ? loggedInUser.groupName + ':' : '' }`;

        workerRef.current.postMessage({ 
          type: 'generatePdf', 
          payload: csvData, 
          pdfInfo: {
            reportName,
            groupInfo: groupNames,
            reportDates: singleDate ? reportDate : getMultipleDatesStr(),
            subAccount,
            fileName: getFileName() 
          }
        })

      } else {
        return
      }
    }

    useEffect(() => {
      if (!open && csvData?.length && selectedExportOption === 'pdf') {
        setTimeout(() => {
          createPdf()
        }, 1000)
      }

      if (csvData.length && (selectedExportOption === 'csv' || selectedExportOption === 'tsv')) {
        setTimeout(() => {
          csvLink.current.link.click()
          setSelectedExportOption('')
          resetExportDropdown()
        })
      } else if (csvData.length && selectedExportOption === 'xlsx') {
        const ws = utils.json_to_sheet(csvData)
        const wb = utils.book_new()
        utils.book_append_sheet(wb, ws, getXLSXFileName());
        writeFile(wb, `${getFileName()}.xlsx`)
        setSelectedExportOption('')
        resetExportDropdown()
      }
    }, [csvData])

    useEffect(() => {
      if (multiCsvData.length === 3 && selectedExportOption === 'csv') {
        downloadMultipleCSVsAsZip();
        setSelectedExportOption('');
        resetExportDropdown();
      } else if (multiCsvData.length === 3 && selectedExportOption === 'xlsx') {
        downloadMultiSheetXLSX();
        setSelectedExportOption('');
        resetExportDropdown();
      }
    }, [multiCsvData])

    useEffect(() => {
      if (exportFocused) {
        labelRef.current?.classList.add(inputLabelClasses.focused);
      }

    }, [exportFocused])

    const resetExportDropdown = () =>{
      setTimeout(() => {
        dropdownRef.current?.classList.remove(inputBaseClasses.focused);
        labelRef.current?.classList.remove(inputLabelClasses.focused);
        labelRef.current?.classList.add(classes.exportSelect);
      }, 50)
      setCsvData([])
      setExportFocused(false)
    }

      const escapeCsvValue = (value) => {
        if (value === null || value === undefined) {
            return '';
        }

        const stringValue = `${value}`;

        // Check if value contains comma, quote, newline, or carriage return
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r')) {
            // Escape quotes by doubling them and wrap entire value in quotes
            return `"${stringValue.replace(/"/g, '""')}"`;
        }

        return stringValue;
      };

    const convertArrayToCSV = (data) => {
        if (!data || data.length === 0) return '';
        const headers = Object.keys(data[0]);

        // escape headers that contain special characters
        const headerCsvRow = headers.map(header => escapeCsvValue(header.replace(/\n/g, ' '))).join(',')

        const dataCsvRows = data.map(row =>
                headers.map(header => escapeCsvValue(row[header])).join(',')
            );

        const csvRows = [
            headerCsvRow,
            ...dataCsvRows
        ];

        return csvRows.join('\n');
    };

    const downloadMultipleCSVsAsZip = async () => {
        const zip = new JSZip();

        // Process each CSV file - generate CSV content directly from data
        for (let i = 0; i < multiCsvData.length; i++) {
            const csvItem = multiCsvData[i];

            if (csvItem?.data && csvItem.data.length > 0) {
                const csvContent = convertArrayToCSV(csvItem.data);
                const fileName = `${csvItem.fileName} ${getDateRangeWithUTCOffset()}.csv`;
                zip.file(fileName, csvContent);
            }
        }

        const zippedBlob = await zip.generateAsync({ type: 'blob' });
        saveAs(zippedBlob, `Audit Report - ${getDateRangeWithUTCOffset()}.zip`);
    };

    const downloadMultiSheetXLSX = () => {
        const wb = utils.book_new();

        // Add each data set as a separate sheet
        for (let i = 0; i < multiCsvData.length; i++) {
            const csvItem = multiCsvData[i];

            if (csvItem?.data && csvItem.data.length > 0) {
                // Convert data array to worksheet
                const ws = utils.json_to_sheet(csvItem.data);

                // Create sheet name from fileName (remove trailing " - ")
                const sheetName = csvItem.fileName.replace(/\s*-\s*$/, '').replace('Audit Report - ', '');

                // Add sheet to workbook
                utils.book_append_sheet(wb, ws, sheetName);
            }
        }

        // Generate filename with date range
        const fileName = `Audit Report - ${getDateRangeWithUTCOffset()}.xlsx`;

        // Write file
        writeFile(wb, fileName);
    };

    return(
      <Box display="flex" justifyContent="flex-end">
        {buildingPdf || exportInProcess
        ?
          (<Button 
            disabled 
            sx={{...props.style, gap: "4px"}}
            variant={props.variant ? props.variant : "text"} 
            color={props.color ? props.color : "inherit"} 
            className={props.variant === 'contained' ? cx(classes.redesignBtn) : cx(classes.redesignSecondaryBtn)}>
              <CircularProgress size={20} className={classes.actionIcon}></CircularProgress>
              {t('export')}
          </Button>)

          : !exportInProcess && !pdfOption && !xlsxOption && !pdfDisabledReason
          ? 
          (<Button
            sx={props.style}
            variant={props.variant ? props.variant : "text"} 
            color={props.color ? props.color : "inherit"} 
            onClick={handleExportButtonClick}
            disabled={disableExport}
            data-testid={"fdr_btn_export"}
            className={props.variant === 'contained' ? cx(classes.redesignBtn) : cx(classes.redesignSecondaryBtn)}>
              <SvgIcon className={classes.actionIcon}>
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-cloud-download-icon lucide-cloud-download">
                  <path className={"arrowLeft"} d="M12 13v8l-4-4"/>
                  <path className={"arrowRight"} d="m12 21 4-4"/>
                  <path d="M4.393 15.269A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.436 8.284"/>
                </svg>
              </SvgIcon>
              {t('export')}
          </Button>) : 


          <div>
            <Button
              disabled={buildingPdf || exportInProcess || disableExport}
              variant={props.variant} 
              color={props.color} 
              onClick={handleMenuClick} 
              data-testid={"fdr_btn_export_menu"}
              className={props.variant === 'contained' ? cx(classes.redesignBtn) : cx(classes.redesignSecondaryBtn)}>
                <SvgIcon className={classes.actionIcon}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-cloud-download-icon lucide-cloud-download">
                    <path className={"arrowLeft"} d="M12 13v8l-4-4"/>
                    <path className={"arrowRight"} d="m12 21 4-4"/>
                    <path d="M4.393 15.269A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.436 8.284"/>
                  </svg>
                </SvgIcon>
                {t('export')}
            </Button>
            <Menu
              slotProps={{
                paper: {
                  sx: {
                    width: "208px",
                    borderRadius: "6px",
                    backgroundColor: 'var(--neutral-2)',
                    border: '1px solid var(--color-border)',
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'var(--accent-11)'
                    },
                    '& .MuiMenuItem-root': {
                      backgroundColor: 'var(--neutral-2)'
                    },
                    '& .MuiMenuItem-root:hover': {
                      backgroundColor: 'rgba(255,255,255, 0.1)'
                    },
                  },
                }
              }}
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
            >
              {
                xlsxOption ? ['xlsx', reportName === 'Audit Log Report' ? 'tsv' : 'csv'].map((option) => {
                  return <MenuItem key={option} data-testid = {`fdr_export_${option}`} onClick={()=>handleExportMenuUpdate(option)} value={option}>{ exportInProcess ? 'Loading...' : option}</MenuItem>
                }) :  ['csv', 'pdf'].map((option) => {
                  if (option === 'pdf') {
                    if (!pdfOption && pdfDisabledReason) {
                      return (
                        <Tooltip key={option} title={<Typography variant="h6" style={{ padding: '5px 8px'}}>{pdfDisabledReason}</Typography>} placement="top" arrow>
                          <span>
                            <MenuItem disabled data-testid={'fdr_export_pdf'} value={option} style={{ pointerEvents: 'none' }}>pdf</MenuItem>
                          </span>
                        </Tooltip>
                      );
                    }
                    return <MenuItem key={option} data-testid={'fdr_export_pdf'} onClick={()=>handleExportMenuUpdate(option)} value={option}>
                      { buildingPdf ? 'Loading...' : !buildingPdf && reportName === 'Sub-Account Financials Report' && !mobileDevice ? 
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                         <p>{option}</p>
                         <div>
                            <ClickAwayListener onClickAway={() => setShowExportInfo(false)}>
                              <Tooltip
                                placement={'top'}
                                onOpen={() => setShowExportInfo(!showExportInfo)}
                                onClose={() => setShowExportInfo(false)}
                                open={showExportInfo}
                                title={
                                  <Typography variant="h6" style={{ padding: '5px 8px'}}>
                                    If exporting a multi-day report as a PDF using the "Daily" time interval option, data will be
                                    aggregated by day.
                                  </Typography>
                                }
                                arrow
                              >
                                  <InfoIcon
                                    size={20}
                                    style={{
                                      marginBottom: theme.spacing(-0.5),
                                      marginLeft: theme.spacing(0.5),
                                      cursor: 'pointer'
                                    }}
                                  />

                              </Tooltip>
                            </ClickAwayListener>
                         </div>     
                      </Box>
                      : <span>{option}</span>}
                      </MenuItem>
                  } else {
                    return <MenuItem key={option} data-testid={'fdr_export_csv'} onClick={()=>handleExportMenuUpdate(option)}  value={option}>{ exportInProcess ? 'Loading...' : option}</MenuItem>
                  }
                })
              }
            </Menu>
          </div>
        }
        <CSVLink
          // Prepend a UTF-8 BOM so Excel detects the encoding and renders
          // non-ASCII location names (e.g. "São Paulo") correctly
          uFEFF={true}
          data={csvData}
          filename={reportName === "Audit Log Report" ?  `${getFileName()}.tsv` : `${getFileName()}.csv`}
          ref={csvLink}
          target='_blank'
          separator={reportName === "Audit Log Report" ? '\t' : ','}
        />
        </Box>
    );
}

export default ExportCSVComponent
